/**
 * Demo Data — CaraLoom Controlled Demo Environment (iter 179).
 *
 * ALL DATA IS FICTIONAL. Do not use these names for any production purpose
 * and do not attribute any fictional performance to real organizations.
 *
 * Fictional hospital: Meridian Regional Medical Center (Northern Virginia).
 * The demo environment does NOT read or write anything to the production
 * MongoDB — this module is the entire source of truth for the demo, held
 * in memory and copied at reset time from `INITIAL_STATE`.
 */

const HOSPITAL = {
  name: 'Meridian Regional Medical Center',
  location: 'Northern Virginia',
};

const PROVIDERS_BY_TYPE = {
  'Home Health': [
    { id: 'p-willow',  name: 'Willow Home Health',    area: 'Available', tag: 'Appropriate Option' },
    { id: 'p-north',   name: 'NorthStar Home Care',   area: 'Available', tag: 'Appropriate Option' },
    { id: 'p-horizon', name: 'Horizon Home Health',   area: 'Available', tag: 'Appropriate Option' },
  ],
  Hospice: [
    { id: 'p-harbor',   name: 'Harbor Hospice',        area: 'Available', tag: 'Appropriate Option' },
    { id: 'p-summit',   name: 'Summit Hospice Care',   area: 'Available', tag: 'Appropriate Option' },
  ],
  SNF: [
    { id: 'p-oakridge', name: 'Oakridge Skilled Nursing', area: 'Available', tag: 'Appropriate Option' },
    { id: 'p-brookdale',name: 'Brookdale Rehab & Nursing',area: 'Available', tag: 'Appropriate Option' },
  ],
  IRF: [
    { id: 'p-potomac',  name: 'Potomac Rehabilitation Center', area: 'Available', tag: 'Appropriate Option' },
  ],
  LTACH: [
    { id: 'p-capital',  name: 'Capital LTACH', area: 'Available', tag: 'Appropriate Option' },
  ],
};

// 14 fictional transitions. Statuses: confirmed | in_progress | needs_attention.
// Iter 179 · Eleanor Carter (tx-1) is the primary demo story. Presenters
// can swap her archetype mid-meeting via `useDemo().archetype` — see
// ELEANOR_ARCHETYPES below.
const TRANSITIONS = [
  { id: 'tx-1',  patient: 'Eleanor Carter',   age: 78, need: 'Home Health', status: 'confirmed',       provider: 'Willow Home Health',       expected: 'Tomorrow', tone: 'on-track',    dischargedOn: '2026-02-19' },
  { id: 'tx-2',  patient: 'Robert Hayes',     age: 72, need: 'SNF',          status: 'in_progress',    provider: null,                        expected: null,       tone: 'in-motion',   dischargedOn: '2026-02-19' },
  { id: 'tx-3',  patient: 'Margaret Wilson',  age: 84, need: 'Hospice',      status: 'confirmed',       provider: 'Harbor Hospice',            expected: 'Today',    tone: 'on-track',    dischargedOn: '2026-02-19' },
  { id: 'tx-4',  patient: 'James Bennett',    age: 66, need: 'IRF',          status: 'needs_attention',provider: null,                        expected: null,       tone: 'attention',   dischargedOn: '2026-02-19' },
  { id: 'tx-5',  patient: 'Patricia Morgan',  age: 81, need: 'Home Health',  status: 'confirmed',       provider: 'NorthStar Home Care',       expected: 'Tomorrow', tone: 'on-track',    dischargedOn: '2026-02-19' },
  { id: 'tx-6',  patient: 'William Brooks',   age: 74, need: 'SNF',          status: 'confirmed',       provider: 'Oakridge Skilled Nursing',  expected: 'Today',    tone: 'on-track',    dischargedOn: '2026-02-19' },
  { id: 'tx-7',  patient: 'Helen Foster',     age: 89, need: 'Hospice',      status: 'confirmed',       provider: 'Summit Hospice Care',       expected: 'Today',    tone: 'on-track',    dischargedOn: '2026-02-19' },
  { id: 'tx-8',  patient: 'Thomas Reed',      age: 70, need: 'Home Health',  status: 'in_progress',    provider: null,                        expected: null,       tone: 'in-motion',   dischargedOn: '2026-02-19' },
  { id: 'tx-9',  patient: 'Gloria Sanders',   age: 76, need: 'SNF',          status: 'confirmed',       provider: 'Brookdale Rehab & Nursing', expected: 'Tomorrow', tone: 'on-track',    dischargedOn: '2026-02-19' },
  { id: 'tx-10', patient: 'Arthur Coleman',   age: 68, need: 'LTACH',        status: 'confirmed',       provider: 'Capital LTACH',             expected: 'In 2 days',tone: 'on-track',    dischargedOn: '2026-02-19' },
  { id: 'tx-11', patient: 'Maria Rivera',     age: 79, need: 'Home Health',  status: 'confirmed',       provider: 'Horizon Home Health',       expected: 'Tomorrow', tone: 'on-track',    dischargedOn: '2026-02-19' },
  { id: 'tx-12', patient: 'Samuel Davis',     age: 71, need: 'Hospice',      status: 'confirmed',       provider: 'Harbor Hospice',            expected: 'Today',    tone: 'on-track',    dischargedOn: '2026-02-19' },
  { id: 'tx-13', patient: 'Frances Whitaker', age: 83, need: 'IRF',          status: 'confirmed',       provider: 'Potomac Rehabilitation Center', expected: 'Tomorrow', tone: 'on-track', dischargedOn: '2026-02-19' },
  { id: 'tx-14', patient: 'Charles Palmer',   age: 77, need: 'Home Health',  status: 'confirmed',       provider: 'Willow Home Health',        expected: 'Tomorrow', tone: 'on-track',    dischargedOn: '2026-02-19' },
];

const KPIS = {
  active: 18,
  confirmed: 14,
  in_progress: 3,
  needs_attention: 1,
};

// Iter 179 · Archetype switcher for the primary demo story. Same URL, same
// UI shell — only Eleanor's clinical need + receiving provider + expected
// window swap. Presenters use this to tune the pitch to the room.
const ELEANOR_ARCHETYPES = {
  'home-health': {
    key:      'home-health',
    label:    'Home Health',
    caption:  'Return home with skilled visits.',
    need:     'Home Health',
    provider: 'Willow Home Health',
    expected: 'Tomorrow',
    story:    'Eleanor recovers at home with a skilled care team on day one.',
  },
  hospice: {
    key:      'hospice',
    label:    'Hospice',
    caption:  'Comfort-focused care at the right moment.',
    need:     'Hospice',
    provider: 'Harbor Hospice',
    expected: 'Today',
    story:    'Eleanor and her family choose a comfort-focused plan of care.',
  },
  snf: {
    key:      'snf',
    label:    'SNF',
    caption:  'Short-stay skilled rehab before returning home.',
    need:     'SNF',
    provider: 'Oakridge Skilled Nursing',
    expected: 'Tomorrow',
    story:    'Eleanor completes short-stay skilled rehab before heading home.',
  },
};

const INSIGHTS = {
  median_time_to_placement: '1h 42m',
  confirmed_placement_pct:  '78%',
  transition_visibility_pct:'94%',
  needs_attention_pct:      '6%',
  disclaimer: 'Illustrative demo data — not customer performance results.',
};

const CHART_STATUS = [
  { label: 'Confirmed',        value: 14, color: '#10b981' },
  { label: 'In Progress',      value:  3, color: '#38bdf8' },
  { label: 'Needs Attention',  value:  1, color: '#f97316' },
];

const CHART_BY_SETTING = [
  { label: 'Home Health', value: 6, color: '#10b981' },
  { label: 'Hospice',     value: 3, color: '#8b5cf6' },
  { label: 'SNF',         value: 3, color: '#f59e0b' },
  { label: 'IRF',         value: 1, color: '#ef4444' },
  { label: 'LTACH',       value: 1, color: '#0ea5e9' },
];

const CHART_TREND = [
  { label: 'W1', value: 128 },
  { label: 'W2', value: 116 },
  { label: 'W3', value: 108 },
  { label: 'W4', value:  98 },
  { label: 'W5', value: 102 },
  { label: 'W6', value:  92 },
  { label: 'W7', value: 102 },
  { label: 'W8', value:  87 },
];

// Presenter shortcuts (labels + testids used by the Presenter Bar).
const PRESENTER_STOPS = [
  { key: 'overview',   label: 'Executive Overview',      to: '/demo/preview' },
  { key: 'eleanor',    label: "Eleanor's Transition",   to: '/demo/preview/patient/tx-1' },
  { key: 'placement',  label: 'Appropriate Options',    to: '/demo/preview/patient/tx-1?view=placement' },
  { key: 'confirmed',  label: 'Placement Confirmed',    to: '/demo/preview/patient/tx-1?view=confirmed' },
  { key: 'network',    label: 'Network',                to: '/demo/preview/network' },
  { key: 'insights',   label: 'Insights',               to: '/demo/preview/insights' },
  { key: 'pilot',      label: 'Pilot Discussion',       to: '/demo/preview/pilot' },
];

const INITIAL_STATE = {
  hospital: HOSPITAL,
  providers_by_type: PROVIDERS_BY_TYPE,
  transitions: TRANSITIONS,
  kpis: KPIS,
  insights: INSIGHTS,
  charts: {
    status: CHART_STATUS,
    by_setting: CHART_BY_SETTING,
    trend: CHART_TREND,
  },
  presenter_stops: PRESENTER_STOPS,
};

// Deep-copy on read so consumers can mutate their local state freely
// without polluting the "original" fixture (used by Reset Demo).
export function loadInitialDemoState() {
  return JSON.parse(JSON.stringify(INITIAL_STATE));
}

// Iter 179 · exported so the DemoContext + DemoScreens can apply the
// current archetype to Eleanor's card.
export { ELEANOR_ARCHETYPES };
export const ELEANOR_ARCHETYPE_KEYS = Object.keys(ELEANOR_ARCHETYPES);

export const DEMO_META = {
  environment_label: 'DEMO ENVIRONMENT — FICTIONAL DATA',  watermark:         'CARALOOM DEMO',
};
